import { getSignOutUrl, IAuthenticatedUserInfo, isMsa } from '@notes/webintegrations-common'
import { IO365ShellShim, NavBarLinkData, ShellLoadErrorEvent, SignOutEventArgs } from '@suiteux/suiteux-shell'
import * as React from 'react'
import * as ReactDOM from 'react-dom'
import { isInternalUser } from '../lib/isInternalUser'
import { LocalizationService } from '../localization/LocalizationService'
import { getForceSignInUrl } from '../shared/auth/getSignInUrl'
import { Constants } from '../shared/Constants'
import { Errors } from '../shared/logger/Errors'
import { Log } from '../shared/logger/Log'
import { addScriptToBody } from './addScriptToBody'

function getReactShellHost(): string {
  return 'https://shellprod.msocdn.com' // PROD
  // return 'https://shellppe.msocdn.com' // PPE
}

function getReactShellUrl(): string {
  return getReactShellHost() + '/api/shellbootstrapper/consumer/oneshell'
}

function onStickyNotesFeedbackClick(): void  {
  window.open(Constants.DOGFOOD_TEAMS_CHAT, '_blank')
}

function getStickyNotesSettingLinks(exportNotesCallback: () => void, userInfo: IAuthenticatedUserInfo): NavBarLinkData[] {
  const exportNotesLabel = LocalizationService.getLocalizedString('ExportNotes.Text')
  const feedbackLabel = LocalizationService.getLocalizedString('stickyNotes.feedback')
  const links =  [
    {
      Id: 'ExportNotesId',
      Text: exportNotesLabel,
      Action: exportNotesCallback,
    },
  ]
  if (isInternalUser(userInfo.upn)) {
    links.push({
      Id: 'FeedbackId',
      Text: feedbackLabel,
      Action: onStickyNotesFeedbackClick,
    })
  }
  return links
}

function getWindowO365Shell(): IO365ShellShim {
  // tslint:disable-next-line:no-any
  const windowObj = window as any
  return windowObj.O365Shell as IO365ShellShim
}

// tslint:disable-next-line:max-func-body-length
export async function renderReactShell(onSignOut: () => void,
  exportNotesClick: () => void, userInfo: IAuthenticatedUserInfo): Promise<void> {
  // We are implementing the React Shell here
  // tslint:disable-next-line:max-line-length
  // https://microsoft.sharepoint.com/teams/shellengintegrations/_layouts/15/WopiFrame.aspx?sourcedoc={b7c609b0-1d40-4d06-b017-9076e6566977}&action=edit&wd=target%28Onboarding%20Engineering%20References.one%7C14ec3df7-c461-45fa-bea8-55f3a1e71325%2FConsumer%20React%20Shell%20Onboarding%7Cd11e4388-a574-406c-ba55-08be052b52e1%2F%29

  // tslint:disable-next-line:no-any
  const windowObj = window as any

  // First, we expose react and reactdom in the global namespace so it gets reused by the shell
  windowObj.React = React
  windowObj.ReactDOM = ReactDOM

  // We add the script to the body and wait for it to be loaded
  await addScriptToBody(getReactShellUrl())

  Log.logger.info('React shell loaded!')

  // Then we call renderasync with the information the header needs
  return new Promise<void>((resolve: () => void, reject: (error: ShellLoadErrorEvent) => void) => {
    const workloadSettingsSubLinks = getStickyNotesSettingLinks(exportNotesClick, userInfo)
    getWindowO365Shell().RenderAsync(
      {
        top: 'TopContainer',
        workloadSettingsSubLinks,
        initialRenderData: {
          ShellBootHost: getReactShellHost(),
          IsConsumer: isMsa(userInfo.authType),
          WorkloadId: 'StickyNotes',
          UserDisplayName: userInfo.name,
          UPN: userInfo.upn,
          CurrentMainLinkElementID: 'StickyNotes',
          Language: LocalizationService.locale,
          Culture: LocalizationService.locale,
          UserID: userInfo.userCid,
        },
        shellDataOverrides: {
          AppHeaderLinkText: 'Sticky Notes',
          AppHeaderLinkUrl: 'https://www.onenote.com/stickynotes',
        },
      },
      () => {
        Log.logger.info('Consumer shell rendered!')
      },
      () => {
        Log.logger.info('Consumer shell loaded!')

        resolve()

        // At this point, the shell is ready to take some input in
        getWindowO365Shell().OnSignOut((event: SignOutEventArgs) => {
          // Prevent the redirect for the default logout url
          event.triggerEvent.preventDefault()

          // Callback to delete indexdb contents, etc
          onSignOut()

          // Redirect to logout url to clear local storage credentials, then redirect back to login page
          window.location.href = getSignOutUrl(getForceSignInUrl(true))
        })
      },
      (error: ShellLoadErrorEvent) => {
        // tslint:disable-next-line:max-line-length
        Log.logger.logUnexpected(Errors.shellLoadingError, `Consumer shell failed to render! ResourceName: ${error.ResourceName} Error message: ${error.Details}`)
        reject(error)
      }
    )
  })
}
